import { useCallback, useMemo, useRef } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import SideMsg from '#/modules/commonComponents/SideMsg';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import { buildSupplierColumns } from '#/modules/srmMgmt/supplier/data/supplierColumns';
import { buildSupplierSideMsgBlocks } from '#/modules/srmMgmt/supplier/data/supplierSideMsgConfig';
import { getSupplierApiErrorMessage } from '#/modules/srmMgmt/supplier/helper/apiError';
import useSupplierList from '#/modules/srmMgmt/supplier/hooks/useSupplierList';

const SupplierListPage = () => {
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.SUPPLIER_CREATE);
  const canUpdate = useCan(PERM.SUPPLIER_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    removeSortAt,
    resetListQuery,
  } = useSupplierList();

  const columns = useMemo(
    () => buildSupplierColumns({
      filters: listQuery.filters || {},
      onFilterChange: setFilter,
      canShowEdit: () => canUpdate,
    }),
    [listQuery.filters, setFilter, canUpdate],
  );

  const sideMsgBlocks = useMemo(
    () => buildSupplierSideMsgBlocks({ canUpdate }),
    [canUpdate],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns),
    [columns],
  );

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.supplierCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">供應商</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getSupplierApiErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋代號或名稱"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={DEFAULT_LIST_SORT_FIELD_LABELS}
              onFilterChange={setFilter}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
            />
          </div>

          <SideMsg
            title="供應商"
            titleValue="name"
            displayData={displayData}
            blocks={sideMsgBlocks}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default SupplierListPage;
